import { useEffect, useRef, useState } from 'react'

import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { useTranslation } from 'react-i18next'

import {
  Badge,
  Box,
  LightModeSemanticColors,
  TertiaryButton,
  Text,
  chakra,
} from '@enechain/ecloud-designsystem'
import { FaCircleQuestionRegular } from '@enechain/ecloud-icons'

import { TABLE_HEADER_CELL_STYLE } from '~/components/common/MasterDataTable'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { iconSize } from '~/constants/iconSize'
import { typography } from '~/constants/typography'
import type { GapSummaryRow } from '~/pages/SupplyGap/models/types'

type Props = {
  rows: GapSummaryRow[]
}

const borderColor = LightModeSemanticColors.Border.lowEmphasis.value

/**
 * 表の体裁は**共通の TH スタイルに寄せる** (2026-09-02 デザイナー指摘「table の
 * DS 準拠確認」)。
 *
 * この表だけ生 `<table>` で `fontSize: sm` (12px) / `padding: 6px 8px` /
 * TH 背景 `Surface.secondary` を直書きしており、 次の 3 点で逸脱していた。
 *
 * 1. `design/CLAUDE.md`「TD のフォントは md 継承がデフォルト。 xs/sm override は
 *    原則しない」に反する (th/td/table すべて sm だった)
 * 2. 生 `<table>` を使う画面も TH は `TABLE_HEADER_CELL_STYLE` を import して
 *    1 箇所に集約する、 という規約が `MasterDataTable` の JSDoc にあり、
 *    実際に 15 ファイルが従っている。 この表だけ外れていた
 * 3. TH 背景が `Surface.secondary` で、 他の全表の `Surface.tableHeader` と違う
 *    (`TABLE_HEADER_CELL_STYLE` が背景を持つので `<tr>` 側の指定は不要)
 *
 * design 8080 (`supply/supply-gap.html`) はこの表を `class="ecl-table"` で
 * 描いており、 共通クラスに寄せる形が既にプロト側の正。
 *
 * `textAlign` は列ごとに変わるので `TABLE_HEADER_CELL_STYLE` に含まれない
 * (呼び出し側で指定する)。 列揃えは元から規約どおり (固定値 center / 数値 right /
 * 可変テキスト left) なので変更しない。
 */
const headerStyle: React.CSSProperties = {
  ...TABLE_HEADER_CELL_STYLE,
}

const cellStyle: React.CSSProperties = {
  ...typography.tableCell,
  // design `.ecl-table td` = `6px 10px`
  padding: '6px 10px',
  borderBottom: `1px solid ${borderColor}`,
  whiteSpace: 'nowrap',
}

/**
 * design (`.sg-guide-wrap` / `.sg-guide-panel`) 準拠のガイドボタン + popover。
 * ecloud に汎用の disclosure popover component が無いため design 同様の
 * 手動開閉 (クリックトグル + 外側クリック/Esc で閉じる) で実装する。
 * (GapChart 見出し側にも同種の実装が要るが、対象ファイルを本コンポーネントと
 *  index.tsx に閉じるため意図的に重複させている。)
 */
const GuidePopover = ({
  panelId,
  buttonLabel,
  ariaLabel,
  title,
  children,
}: {
  panelId: string
  buttonLabel: string
  ariaLabel: string
  title: string
  children: React.ReactNode
}): React.ReactElement => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined

    const handlePointerDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <Box ref={wrapRef} position="relative" display="inline-flex">
      <TertiaryButton
        aria-controls={panelId}
        aria-expanded={open}
        aria-label={ariaLabel}
        // ガイドの ? は 14px (UI 辞書 guide-popover.md)
        leftIcon={
          <FaCircleQuestionRegular
            boxSize={iconSize.guideButton}
            aria-hidden="true"
          />
        }
        onClick={() => setOpen((v) => !v)}
        size="sm"
      >
        {buttonLabel}
      </TertiaryButton>
      {open && (
        <Box
          position="absolute"
          zIndex={50}
          top="calc(100% + 6px)"
          left={0}
          width="380px"
          maxWidth="90vw"
          padding={4}
          background="Surface.primary"
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="lg"
          boxShadow="lg"
          id={panelId}
          role="dialog"
        >
          <Text marginBottom={2} fontWeight="semibold">
            {title}
          </Text>
          {children}
        </Box>
      )}
    </Box>
  )
}

export const SubPeriodSummaryTable = ({
  rows,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()

  if (rows.length === 0) return null

  return (
    <Box
      flexDirection="column"
      display="flex"
      overflow="hidden"
      width="100%"
      height="100%"
      background="Surface.primary"
      borderWidth="1px"
      // 器は UI 辞書 card.md の `.card` (枠 Border.lowEmphasis・角丸 lg・影 shadow-sm)。
      // 見出しは `.card-header` (余白 16px・下線)
      borderColor={borderColor}
      borderRadius="lg"
      boxShadow="shadSm"
    >
      {/* カードヘッダ: タイトル + ガイド + 単位 (design L310-330 準拠) */}
      <Box
        alignItems="center"
        flexWrap="wrap"
        flexShrink={0}
        gap={2}
        display="flex"
        padding={4}
        borderColor={borderColor}
        borderBottomWidth="1px"
      >
        {/* カード見出し。design `.card-header` = 16px/600 (「16px 統一」)。
            `label` は md (14px) のフォームラベル role なので当てない
            (2026-09-03 デザイナー指摘)。 */}
        <Text textStyle="sectionTitle">
          {t('page:supplyGap.summaryTable.title')}
        </Text>
        <GuidePopover
          ariaLabel={t('page:supplyGap.summaryTable.guideAriaLabel')}
          buttonLabel={t('page:supplyGap.guideButton')}
          panelId="sg-summary-guide-panel"
          title={t('page:supplyGap.summaryTable.guideTitle')}
        >
          {/* ガイド本文 (UI 辞書 guide-popover.md `.ecl-guide-popover-body` = md・mid-emphasis) */}
          <Text marginBottom={2} color="Text.midEmphasis" fontSize="md">
            {t('page:supplyGap.summaryTable.guideIntro')}
          </Text>
          <Box
            as="ul"
            paddingLeft="1.2em"
            color="Text.midEmphasis"
            fontSize="md"
          >
            <li>{t('page:supplyGap.summaryTable.guideSafeSell')}</li>
            <li>{t('page:supplyGap.summaryTable.guideSafeBuy')}</li>
            <li>{t('page:supplyGap.summaryTable.guidePeakOffpeak')}</li>
            <li>{t('page:supplyGap.summaryTable.guideApproxNote')}</li>
          </Box>
        </GuidePopover>
        <Text
          textStyle="captionNormal"
          marginLeft="auto"
          color="Text.midEmphasis"
        >
          {t('page:supplyGap.summaryTable.unitMw')}
        </Text>
      </Box>

      {/* テーブル本体 (design L334-347 準拠: エリア列を持つ横断表) */}
      <Box flex={1} overflow="auto" minHeight={0}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            {/* 背景は `TABLE_HEADER_CELL_STYLE` (= `TABLE_HEADER_BG`) が持つ。
                ここで `Surface.secondary` を重ねると他の表と色が違ってしまう。 */}
            <tr>
              <th style={{ ...headerStyle, textAlign: 'left' }}>
                {t('page:supplyGap.summaryTable.area')}
              </th>
              <th style={{ ...headerStyle, textAlign: 'left' }}>
                {t('page:supplyGap.summaryTable.period')}
              </th>
              <th style={{ ...headerStyle, textAlign: 'center' }}>
                {t('page:supplyGap.summaryTable.status')}
              </th>
              <th style={{ ...headerStyle, textAlign: 'center' }}>
                {t('page:supplyGap.summaryTable.tradeType')}
              </th>
              <th style={{ ...headerStyle, textAlign: 'right' }}>
                {t('page:supplyGap.summaryTable.baseMw')}
              </th>
              <th style={{ ...headerStyle, textAlign: 'right' }}>
                {t('page:supplyGap.summaryTable.peakMw')}
              </th>
              <th style={{ ...headerStyle, textAlign: 'right' }}>
                {t('page:supplyGap.summaryTable.offpeakMw')}
              </th>
              <th style={{ ...headerStyle, textAlign: 'right' }}>
                {t('page:supplyGap.summaryTable.safeSellPrecise')}
              </th>
              <th style={{ ...headerStyle, textAlign: 'right' }}>
                {t('page:supplyGap.summaryTable.requiredBuyPrecise')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              // design (L862-863): 数値セルの色は状態と揃える (ロング=positiveHigh / ショート=negativeMid)
              const numCellStyle: React.CSSProperties = {
                ...cellStyle,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
                color: row.isLong
                  ? LightModeSemanticColors.Text.positiveHigh.value
                  : LightModeSemanticColors.Text.negativeMid.value,
              }
              // 偶数行の縞と hover (UI 辞書 table.md §0 `.ecl-table tbody tr`)
              return (
                <chakra.tr
                  key={idx}
                  background={
                    idx % 2 === 1 ? 'Surface.secondary' : 'Surface.primary'
                  }
                  _hover={{ background: 'Hover.lowEmphasis' }}
                >
                  <td style={{ ...cellStyle, textAlign: 'left' }}>
                    {row.areaLabel}
                  </td>
                  <td
                    style={{
                      ...cellStyle,
                      textAlign: 'left',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {row.periodLabel}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    <Badge status={row.isLong ? 'positive' : 'negative'}>
                      {row.isLong
                        ? t('page:supplyGap.summaryTable.long')
                        : t('page:supplyGap.summaryTable.short')}
                    </Badge>
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    {/* 生の DS TradeLabel ではなく共通ラッパを使う: セル中央寄せ
                        (inline-block 化) と aria-label/role="img" を共通部品側に
                        集約するため (2026-09-01 デザイナー指摘)。 */}
                    <TradeSideLabel side={row.isLong ? Side.SELL : Side.BUY} />
                  </td>
                  <td style={numCellStyle}>{row.baseMw.toFixed(1)}</td>
                  <td style={numCellStyle}>{row.peakMw.toFixed(1)}</td>
                  <td style={numCellStyle}>{row.offpeakMw.toFixed(1)}</td>
                  {/* 精密値: ロング区間は安全売却量 (余剰の最小)、ショート区間は
                      必要買い量 (不足の最大) のみが有意。非該当側は "—" で区別する
                      (backend は非該当側を 0 で返すが 0 MW 表示は誤読を招くため)。 */}
                  <td style={numCellStyle}>
                    {row.isLong ? row.safeSellMw.toFixed(1) : '—'}
                  </td>
                  <td style={numCellStyle}>
                    {row.isLong ? '—' : row.requiredBuyMw.toFixed(1)}
                  </td>
                </chakra.tr>
              )
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  )
}
